"use client";

import { useState } from "react";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { ChevronRight, PanelLeft, X } from "lucide-react";

import { MODULOS } from "@/lib/modulos";
import { cn } from "@/lib/utils";

/**
 * El árbol de navegación del Centro de ayuda: los 5 módulos con todas sus guías, siempre a la vista.
 *
 * 🔑 **Antes no se podía saltar de un módulo a otro sin volver a la portada.** La única navegación era
 * un `aside` en la página del artículo con **los hermanos de su propio módulo**; para pasar de una guía
 * de POS a una de Contabilidad había que subir, volver al índice y entrar otra vez. Con 107 artículos
 * eso ya no da.
 *
 * ⚠️ **Los 107 enlaces se renderizan siempre, plegados o no, y eso es deliberado.** Un módulo cerrado
 * es un `<details>` sin `open`: el navegador lo oculta pero **el marcado está en el HTML**, que es lo
 * que necesitan Google y quien navegue sin JavaScript. Es la misma lección que dejaron las preguntas
 * frecuentes del landing, donde las 34 respuestas existían solo dentro del JSON-LD porque el acordeón
 * no renderizaba lo cerrado. Comprobable: `curl -s https://ayuda.vecipp.com/ | grep -c 'href="/pos/'`.
 *
 * ⚠️ **Es un componente de cliente por el resaltado, no por el contenido.** Necesita `usePathname()`
 * para saber qué guía está abierta; Next lo renderiza igual en el servidor, así que el HTML sale
 * completo. No mover los datos a `useEffect` o se vacía para los rastreadores.
 */

export type ModuloConArticulos = {
  id: string;
  articulos: { slug: string; titulo: string }[];
};

export function ArbolNavegacion({ modulos }: { modulos: ModuloConArticulos[] }) {
  const ruta = usePathname();
  const moduloActual = ruta.split("/")[1] ?? "";
  /* En móvil el árbol vive detrás de un botón. **Se oculta con `hidden`, no desmontándolo**: así el
     marcado sigue en el HTML servido pase lo que pase con el estado. */
  const [abiertoMovil, setAbiertoMovil] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setAbiertoMovil((v) => !v)}
        aria-expanded={abiertoMovil}
        className="mb-4 flex items-center gap-2 rounded-xl border px-3 py-2 font-medium text-sm lg:hidden"
      >
        {abiertoMovil ? <X className="size-4" /> : <PanelLeft className="size-4" />}
        Todas las guías
      </button>

      <nav
        aria-label="Guías del Centro de ayuda"
        /* ⚠️ **El `sticky` y el scroll propio los pone el envoltorio del `layout`, no este `nav`.**
           Estuvieron en los dos a la vez y se estorbaban: dos contenedores de desplazamiento anidados
           dejan el árbol pegado sin poder llegar hasta el último módulo. Acá solo queda si se ve o no. */
        className={cn("lg:block", abiertoMovil ? "mb-6 block" : "hidden")}
      >
        <ul className="flex flex-col gap-1">
          {MODULOS.map((modulo) => {
            const datos = modulos.find((m) => m.id === modulo.id);
            const articulos = datos?.articulos ?? [];
            const esActual = moduloActual === modulo.id;
            const Icon = modulo.icon;
            return (
              <li key={modulo.id}>
                <details open={esActual} className="group">
                  <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-muted [&::-webkit-details-marker]:hidden">
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                    <Icon className={cn("size-4 shrink-0", esActual ? "text-primary" : "text-muted-foreground")} />
                    <span className={cn("flex-1 font-medium", esActual && "text-primary")}>{modulo.nombre}</span>
                    <span className="text-[0.7rem] text-muted-foreground tabular-nums">{articulos.length}</span>
                  </summary>

                  {articulos.length ? (
                    <ul className="mt-0.5 mb-2 ml-[1.05rem] flex flex-col border-l">
                      {articulos.map((articulo) => {
                        const href = `/${modulo.id}/${articulo.slug}`;
                        const activo = ruta === href;
                        return (
                          <li key={articulo.slug}>
                            {/* 🔑 **El activo es una píldora rellena, no un borde de color.** Es lo
                                que hace la referencia y no es capricho: en un árbol de 107 enlaces con
                                filete izquierdo, un borde más marcado se pierde entre los demás. Una
                                superficie rellena se encuentra de un vistazo aunque haya que desplazar
                                la columna. */}
                            <Link
                              href={href}
                              aria-current={activo ? "page" : undefined}
                              className={cn(
                                "-ml-px block rounded-md border-l py-1.5 pr-2 pl-3 text-sm leading-snug transition-colors",
                                activo
                                  ? "border-transparent bg-muted font-medium text-foreground"
                                  : "border-transparent text-muted-foreground hover:border-border hover:bg-muted hover:text-foreground",
                              )}
                            >
                              {articulo.titulo}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="mt-0.5 mb-2 ml-[1.05rem] border-l py-1.5 pl-3 text-muted-foreground text-sm">
                      Muy pronto.
                    </p>
                  )}
                </details>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
